import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Users, FileText, Lock, CreditCard, Flag, TrendingUp, 
  Search, Ban, CheckCircle, Trash2, Crown, Loader2,
  DollarSign, AlertTriangle
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [worries, setWorries] = useState([]);
  const [reports, setReports] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, postsRes, worriesRes, reportsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`),
        axios.get(`${API}/api/admin/users?limit=50`),
        axios.get(`${API}/api/admin/posts?limit=50`),
        axios.get(`${API}/api/admin/worries?limit=50`),
        axios.get(`${API}/api/admin/reports?status=pending`),
        axios.get(`${API}/api/admin/payments?limit=50`)
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setPosts(postsRes.data.posts);
      setWorries(worriesRes.data.worries);
      setReports(reportsRes.data.reports);
      setPayments(paymentsRes.data.transactions);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleBanUser = async (userId, isBanned) => {
    try {
      await axios.post(`${API}/api/admin/users/${userId}/${isBanned ? 'unban' : 'ban'}`);
      setUsers(users.map(u => u.id === userId ? { ...u, is_banned: !isBanned } : u));
      toast.success(isBanned ? 'User unbanned' : 'User banned');
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleGrantPremium = async (userId) => {
    try {
      await axios.post(`${API}/api/admin/users/${userId}/premium?days=30`);
      toast.success('Premium granted for 30 days');
      fetchData();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/api/admin/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleDeleteWorry = async (worryId) => {
    if (!window.confirm('Delete this anonymous post?')) return;
    try {
      await axios.delete(`${API}/api/admin/worries/${worryId}`);
      setWorries(worries.filter(w => w.id !== worryId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleResolveReport = async (reportId, action) => {
    try {
      await axios.post(`${API}/api/admin/reports/${reportId}/resolve?action=${action}`);
      setReports(reports.filter(r => r.id !== reportId));
      toast.success('Report resolved');
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F2]">
        <Header />
        <div className="flex justify-center items-center pt-32">
          <Loader2 className="h-8 w-8 animate-spin text-[#6B8E7B]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      <Header />
      
      <div className="content-container pt-24">
        <div className="py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-stone-800 font-['Outfit']">Admin Dashboard</h1>
              <p className="text-stone-500">NurseCommunity by Gina Rena</p>
            </div>
            <Badge className="bg-[#E27D60] text-white">Admin</Badge>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white border border-stone-200 rounded-xl mb-6">
              <TabsTrigger value="overview" className="rounded-lg">Overview</TabsTrigger>
              <TabsTrigger value="users" className="rounded-lg">Users</TabsTrigger>
              <TabsTrigger value="posts" className="rounded-lg">Posts</TabsTrigger>
              <TabsTrigger value="worries" className="rounded-lg">Anonymous</TabsTrigger>
              <TabsTrigger value="reports" className="rounded-lg">
                Reports
                {reports.length > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{reports.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="payments" className="rounded-lg">Payments</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <Card className="border-stone-200 rounded-xl">
                  <CardContent className="p-4 text-center">
                    <Users className="h-6 w-6 text-[#6B8E7B] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-800">{stats?.users || 0}</p>
                    <p className="text-xs text-stone-500">Total Users</p>
                  </CardContent>
                </Card>
                <Card className="border-stone-200 rounded-xl">
                  <CardContent className="p-4 text-center">
                    <Crown className="h-6 w-6 text-[#E27D60] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-800">{stats?.premium_users || 0}</p>
                    <p className="text-xs text-stone-500">Premium</p>
                  </CardContent>
                </Card>
                <Card className="border-stone-200 rounded-xl">
                  <CardContent className="p-4 text-center">
                    <FileText className="h-6 w-6 text-[#6B8E7B] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-800">{stats?.posts || 0}</p>
                    <p className="text-xs text-stone-500">Posts</p>
                  </CardContent>
                </Card>
                <Card className="border-stone-200 rounded-xl">
                  <CardContent className="p-4 text-center">
                    <Lock className="h-6 w-6 text-[#E27D60] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-800">{stats?.anonymous_worries || 0}</p>
                    <p className="text-xs text-stone-500">Anonymous</p>
                  </CardContent>
                </Card>
                <Card className="border-stone-200 rounded-xl">
                  <CardContent className="p-4 text-center">
                    <Flag className="h-6 w-6 text-red-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-800">{stats?.pending_reports || 0}</p>
                    <p className="text-xs text-stone-500">Reports</p>
                  </CardContent>
                </Card>
                <Card className="border-stone-200 rounded-xl">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="h-6 w-6 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-800">€{stats?.total_revenue || 0}</p>
                    <p className="text-xs text-stone-500">Revenue</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <div className="grid lg:grid-cols-2 gap-6">
                <Card className="border-stone-200 rounded-xl">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Outfit']">Recent Users</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {users.slice(0, 5).map(u => (
                      <div key={u.id} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
                        <div>
                          <p className="font-medium text-stone-800">{u.name}</p>
                          <p className="text-xs text-stone-500">{u.email}</p>
                        </div>
                        <span className="text-xs text-stone-400">{formatDate(u.created_at)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-stone-200 rounded-xl">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Outfit']">Recent Payments</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {payments.filter(p => p.payment_status === 'paid').slice(0, 5).map(p => (
                      <div key={p.id} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
                        <div>
                          <p className="font-medium text-stone-800">{p.user_email}</p>
                          <p className="text-xs text-stone-500">€{p.amount}</p>
                        </div>
                        <Badge className="bg-green-100 text-green-700">Paid</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card className="border-stone-200 rounded-xl">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <CardTitle className="text-lg font-['Outfit']">Manage Users</CardTitle>
                    <div className="relative flex-1 max-w-xs">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                      <Input
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 rounded-xl"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {filteredUsers.map(u => (
                      <div key={u.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-stone-800">{u.name}</p>
                            {u.role === 'admin' && <Badge className="bg-purple-100 text-purple-700 text-xs">Admin</Badge>}
                            {u.is_premium && <Badge className="bg-[#E27D60]/10 text-[#E27D60] text-xs">Premium</Badge>}
                            {u.is_banned && <Badge className="bg-red-100 text-red-700 text-xs">Banned</Badge>}
                          </div>
                          <p className="text-sm text-stone-500">{u.email}</p>
                          <p className="text-xs text-stone-400">{u.specialization} • Joined {formatDate(u.created_at)}</p>
                        </div>
                        <div className="flex gap-2">
                          {!u.is_premium && u.role !== 'admin' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGrantPremium(u.id)}
                              className="rounded-full text-[#E27D60] border-[#E27D60]"
                            >
                              <Crown className="h-3 w-3 mr-1" />
                              Grant Premium
                            </Button>
                          )}
                          {u.role !== 'admin' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBanUser(u.id, u.is_banned)}
                              className={`rounded-full ${u.is_banned ? 'text-green-600 border-green-600' : 'text-red-600 border-red-600'}`}
                            >
                              {u.is_banned ? <CheckCircle className="h-3 w-3 mr-1" /> : <Ban className="h-3 w-3 mr-1" />}
                              {u.is_banned ? 'Unban' : 'Ban'}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Posts Tab */}
            <TabsContent value="posts">
              <Card className="border-stone-200 rounded-xl">
                <CardHeader>
                  <CardTitle className="text-lg font-['Outfit']">Manage Posts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {posts.map(p => (
                      <div key={p.id} className={`flex items-center justify-between p-3 rounded-xl ${p.is_deleted ? 'bg-red-50' : 'bg-stone-50'}`}>
                        <div className="flex-1">
                          <p className="font-medium text-stone-800">{p.title}</p>
                          <p className="text-sm text-stone-500 line-clamp-1">{p.content}</p>
                          <p className="text-xs text-stone-400">by {p.author_name} • {formatDate(p.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {p.is_deleted && <Badge className="bg-red-100 text-red-700 text-xs">Deleted</Badge>}
                          {!p.is_deleted && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeletePost(p.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Anonymous Worries Tab */}
            <TabsContent value="worries">
              <Card className="border-stone-200 rounded-xl">
                <CardHeader>
                  <CardTitle className="text-lg font-['Outfit']">Manage Anonymous Posts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {worries.map(w => (
                      <div key={w.id} className={`flex items-center justify-between p-3 rounded-xl ${w.is_deleted ? 'bg-red-50' : 'bg-orange-50'}`}>
                        <div className="flex-1">
                          <p className="text-stone-700 line-clamp-2">{w.content}</p>
                          <p className="text-xs text-stone-400 mt-1">{w.support_count || 0} supports • {formatDate(w.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {w.is_deleted && <Badge className="bg-red-100 text-red-700 text-xs">Deleted</Badge>}
                          {!w.is_deleted && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteWorry(w.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports">
              <Card className="border-stone-200 rounded-xl">
                <CardHeader>
                  <CardTitle className="text-lg font-['Outfit']">Pending Reports</CardTitle>
                </CardHeader>
                <CardContent>
                  {reports.length === 0 ? (
                    <div className="text-center py-8 text-stone-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                      <p>No pending reports</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reports.map(r => (
                        <div key={r.id} className="p-4 bg-red-50 rounded-xl border border-red-100">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="h-4 w-4 text-red-500" />
                                <span className="font-medium text-stone-800">{r.reason}</span>
                              </div>
                              <p className="text-sm text-stone-600">{r.details}</p>
                              <p className="text-xs text-stone-400 mt-2">
                                Reported by {r.reporter_name} • {formatDate(r.created_at)}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleResolveReport(r.id, 'dismissed')}
                                className="rounded-full"
                              >
                                Dismiss
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleResolveReport(r.id, 'action_taken')}
                                className="rounded-full bg-red-500 hover:bg-red-600"
                              >
                                Take Action
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments">
              <Card className="border-stone-200 rounded-xl">
                <CardHeader>
                  <CardTitle className="text-lg font-['Outfit']">Payment History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {payments.map(p => (
                      <div key={p.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                        <div>
                          <p className="font-medium text-stone-800">{p.user_email}</p>
                          <p className="text-sm text-stone-500">Session: {p.session_id?.slice(0, 20)}...</p>
                          <p className="text-xs text-stone-400">{formatDate(p.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-stone-800">€{p.amount}</p>
                          <Badge className={p.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {p.payment_status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
